import { TokenWithAmountForDatum, Token_With_Metadata_And_Amount } from '@/src/lib/MayzSmartDB/Commons/index';
import { FundWithDetailsEntity } from '@/src/lib/MayzSmartDB/Entities/index';
import { useTokensStore, useWalletStore } from '@/src/store';
import { showToken_Amount_Rounded } from '@/src/store/global/tokensStoreModelHelpers';
import { INVESTUNIT_MULTIPLIER } from '@/src/utils/constants';
import { useEffect, useState } from 'react';

export interface InputTokensModalProps {
    isOpen?: boolean;
    fund: FundWithDetailsEntity;
    isDeposit: boolean;
    inputTokens: Token_With_Metadata_And_Amount[];
    setInputTokens: (value: Token_With_Metadata_And_Amount[]) => void;
    amount_Tokens_Available_In_Wallet: TokenWithAmountForDatum[];
    amount_Tokens_Available_In_Wallet_Based_On_FT: TokenWithAmountForDatum[];
    isValidInputTokens: boolean;
    isMaxAmountLoaded: boolean;
    isTokensStoreReady: boolean;
}

export default function useInputTokensModal(props: InputTokensModalProps) {
    //--------------------------------------
    const {
        isOpen,
        fund,
        isDeposit,
        inputTokens,
        setInputTokens,
        isValidInputTokens,
        amount_Tokens_Available_In_Wallet,
        amount_Tokens_Available_In_Wallet_Based_On_FT,
        isMaxAmountLoaded,
        isTokensStoreReady,
    } = props;
    //--------------------------------------
    const tokensStore = useTokensStore();
    const walletStore = useWalletStore();
    //--------------------------------------
    const [isOpenModal, setIsOpenModal] = useState(isOpen ?? false);
    //--------------------------------------
    const [isFocus, setIsFocus] = useState<boolean[]>([]);
    const [inputTokens_, setInputTokens_] = useState<Token_With_Metadata_And_Amount[]>([]);
    const [inputTokensStr, setInputTokensStr] = useState<string | undefined>(undefined);
    //--------------------------------------
    const [isValidInputTokensLessThanExisting, setIsValidInputTokensLessThanExisting] = useState<boolean | undefined>(undefined);
    const [isTokensAvailableInUserWallet, setIsTokensAvailableInUserWallet] = useState<boolean | undefined>(undefined);
    //--------------------------------------
    useEffect(() => {
        if (isOpen !== undefined) setIsOpenModal(isOpen);
    }, [isOpen]);

    //--------------------------------------

    useEffect(() => {
        resetForm();
    }, []);

    //--------------------------------------

    useEffect(() => {
        setInputTokens_(inputTokens.map((token) => ({ ...token })));
        const inputTokensStr = `${inputTokens
            .slice(0, 5)
            .map((token) => showToken_Amount_Rounded(tokensStore, token.amount, token.CS, token.TN_Hex))
            .join(' + ')}`;
        setInputTokensStr(inputTokens.length > 5 ? inputTokensStr + ' + ...' : inputTokensStr);
    }, [inputTokens]);

    //--------------------------------------

    useEffect(() => {
        let isValidInputTokensLessThanExisting = false;
        if (isMaxAmountLoaded === true && isTokensStoreReady === true) {
            if (isDeposit === true) {
                if (inputTokens_.length === 0 || amount_Tokens_Available_In_Wallet === undefined || amount_Tokens_Available_In_Wallet.length === 0) {
                    isValidInputTokensLessThanExisting = false;
                } else {
                    isValidInputTokensLessThanExisting = inputTokens_.every((inputToken) => {
                        if (inputToken.amount === 0n) return false;
                        const inWallet = amount_Tokens_Available_In_Wallet.find(([cs, tn, am]) => cs === inputToken.CS && tn === inputToken.TN_Hex && am >= inputToken.amount);
                        return inWallet !== undefined;
                    });
                }
            } else {
                if (inputTokens_.length === 0 || amount_Tokens_Available_In_Wallet_Based_On_FT === undefined || amount_Tokens_Available_In_Wallet_Based_On_FT.length === 0) {
                    isValidInputTokensLessThanExisting = true;
                } else {
                    isValidInputTokensLessThanExisting = inputTokens_.every((inputToken) => {
                        if (inputToken.amount === 0n) return false;
                        const inWallet = amount_Tokens_Available_In_Wallet_Based_On_FT.find(
                            ([cs, tn, am]) => cs === inputToken.CS && tn === inputToken.TN_Hex && am >= inputToken.amount
                        );
                        return inWallet !== undefined;
                    });
                }
            }
        }
        setIsValidInputTokensLessThanExisting(isValidInputTokensLessThanExisting);
    }, [inputTokens_, amount_Tokens_Available_In_Wallet, amount_Tokens_Available_In_Wallet_Based_On_FT]);

    useEffect(() => {
        let isTokensAvailableInUserWallet = false;
        if (isMaxAmountLoaded === true && isTokensStoreReady === true) {
            if (isDeposit === true) {
                if (inputTokens_.length === 0 || amount_Tokens_Available_In_Wallet === undefined || amount_Tokens_Available_In_Wallet.length === 0) {
                    isTokensAvailableInUserWallet = false;
                } else {
                    isTokensAvailableInUserWallet = inputTokens_.every((inputToken) => {
                        const inWallet = amount_Tokens_Available_In_Wallet.find(([cs, tn, am]) => cs === inputToken.CS && tn === inputToken.TN_Hex && am >= 0n);
                        return inWallet !== undefined;
                    });
                }
            } else {
                isTokensAvailableInUserWallet = true;
            }
        }
        setIsTokensAvailableInUserWallet(isTokensAvailableInUserWallet);
    }, [inputTokens_, amount_Tokens_Available_In_Wallet, amount_Tokens_Available_In_Wallet_Based_On_FT]);
    //--------------------------------------

    const setInputToken = (index: number, value: bigint) => {
        //-----------
        // cuando se edita uno, se editan todos proporcionalmente
        //-----------
        const investUnitTokens = fund.investUnit!.investUnitWithADAPrices.iuValuesWithDetailsAndAmount;
        //-----------
        const requiredAmountDecimals = Number(investUnitTokens[index].amount) / INVESTUNIT_MULTIPLIER; // invest unit is mulplied by 100
        const tokenRatio = Number(value) / requiredAmountDecimals;
        //-----------
        const newValues = inputTokens_.map((inputToken, index_) => {
            if (index_ !== index) {
                const inIU = investUnitTokens.find((tokenIU) => tokenIU.CS === inputToken.CS && tokenIU.TN_Hex === inputToken.TN_Hex);
                if (inIU === undefined) return inputToken;
                const requiredAmountDecimals = Number(inIU.amount) / INVESTUNIT_MULTIPLIER; // invest unit is mulplied by 100
                // uso math ceil para asegurar que esa cantidad entera cubre la cantidad de FT que se pueden comprar
                const amount = BigInt(Math.floor(requiredAmountDecimals * tokenRatio));
                return { ...inputToken, amount };
            } else {
                return { ...inputToken, amount: value };
            }
        });
        setInputTokens_(newValues);
    };

    //--------------------------------------

    const isValidInputToken = (index: number): boolean => {
        if (isDeposit === true) {
            if (inputTokens_[index].amount === 0n) return false;
            if (inputTokens_.length === 0 || amount_Tokens_Available_In_Wallet === undefined || amount_Tokens_Available_In_Wallet.length === 0) return true;
            const inWallet = amount_Tokens_Available_In_Wallet.find(([cs, tn, am]) => cs === inputTokens_[index].CS && tn === inputTokens_[index].TN_Hex);
            if (inWallet === undefined) return false;
            return inputTokens_[index].amount <= inWallet[2];
        } else {
            if (inputTokens_[index].amount === 0n) return false;
            if (inputTokens_.length === 0 || amount_Tokens_Available_In_Wallet_Based_On_FT === undefined || amount_Tokens_Available_In_Wallet_Based_On_FT.length === 0) return true;
            const inWallet = amount_Tokens_Available_In_Wallet_Based_On_FT.find(([cs, tn, am]) => cs === inputTokens_[index].CS && tn === inputTokens_[index].TN_Hex);
            if (inWallet === undefined) return false;
            return inputTokens_[index].amount <= inWallet[2];
        }
    };

    //--------------------------------------

    const isFocus_ = (index: number): boolean => {
        return isFocus[index];
    };

    //--------------------------------------

    const getMax = (index: number) => {
        // console.log('getMax');
        if (isDeposit === true) {
            if (inputTokens_.length === 0 || amount_Tokens_Available_In_Wallet === undefined || amount_Tokens_Available_In_Wallet.length === 0) return 0n;
            const inWallet = amount_Tokens_Available_In_Wallet.find(([cs, tn, _am]) => cs === inputTokens_[index].CS && tn === inputTokens_[index].TN_Hex);
            return inWallet === undefined ? 0n : inWallet[2];
        } else {
            if (inputTokens_.length === 0 || amount_Tokens_Available_In_Wallet_Based_On_FT === undefined || amount_Tokens_Available_In_Wallet_Based_On_FT.length === 0) return 0n;
            const inWallet = amount_Tokens_Available_In_Wallet_Based_On_FT.find(([cs, tn, _am]) => cs === inputTokens_[index].CS && tn === inputTokens_[index].TN_Hex);
            return inWallet === undefined ? 0n : inWallet[2];
        }
    };

    //--------------------------------------

    const handleMin = (index: number) => {
        setInputToken(index, 1n);
    };

    const handleMax = (index: number) => {
        setInputToken(index, getMax(index));
    };

    const handleFocus = (index: number) => {
        const newValues = [...isFocus];
        newValues[index] = true;
        setIsFocus(newValues);
    };
    const handleBlur = (index: number) => {
        const newValues = [...isFocus];
        newValues[index] = true;
        setIsFocus(newValues);
    };

    //--------------------------------------

    const handleOpen = () => {
        setIsOpenModal(true);
        resetForm();
    };
    const handleClose = () => {
        setIsOpenModal(false);
        resetForm();
    };
    const resetForm = () => {
        setIsFocus(inputTokens.map(() => false));
        setInputTokens_(inputTokens.map((token) => ({ ...token })));
    };

    const handleConfirm = () => {
        setIsOpenModal(false);
        setInputTokens(inputTokens_.map((token) => ({ ...token })));
    };

    //--------------------------------------
    return {
        tokensStore,
        walletStore,
        isMaxAmountLoaded,
        isTokensStoreReady,
        isOpen,
        fund,
        isDeposit,
        inputTokens: inputTokens_,
        inputTokensStr,
        setInputTokens,
        isValidInputTokens,
        isValidInputTokensLessThanExisting,
        isTokensAvailableInUserWallet,
        isFocus: isFocus_,
        isValidInputToken,
        setInputToken,
        handleFocus,
        handleBlur,
        handleMin,
        getMax,
        handleMax,
        isOpenModal,
        handleClose,
        handleConfirm,
        handleOpen,
    };
    //--------------------------------------
}
